/**
 * `/doc/:id`'s `beforeLoad` (1.X deliverable 12). When the route opens a
 * document it starts `docs.get` (`prefetchDoc`) before it awaits the session
 * check, so the document's round trip runs beside `auth.me`'s instead of after
 * it. It marks the open trace's `before-load` and `auth-me` phases
 * (deliverable 11).
 *
 * Opening a document: `cause: 'enter'` (the route was not shown), or
 * `cause: 'stay'` with another id than the one shown. TanStack Router 1.171
 * gives `stay` to every navigation that keeps the route, so a mention click
 * from one document to another is a `stay` with a new id. A hash change in the
 * same document (a `stay` with the same id) and a preload prefetch nothing.
 */
import type { MeResponse } from '@codex/contracts';
import { requireSession } from '../auth/require-session.js';
import { markOpen } from './open-trace.js';
import { prefetchDoc } from './prefetch.js';

export interface DocBeforeLoadContext {
  readonly params: { readonly id: string };
  readonly location: { readonly pathname: string };
  readonly cause: 'enter' | 'stay' | 'preload';
}

export interface DocBeforeLoadDeps {
  readonly prefetch: (docId: string) => void;
  readonly requireSession: (pathname: string) => Promise<MeResponse>;
  /** The id the route last loaded for (not a preload); the module's own by default. */
  readonly shown?: { id: string | null };
}

const SHOWN: { id: string | null } = { id: null };

const DEFAULT_DEPS: DocBeforeLoadDeps = {
  prefetch: (docId) => prefetchDoc(docId),
  requireSession,
};

/** Whether this load opens a document (and so fetches it beside the session check). */
export function opensDocument(context: DocBeforeLoadContext, shownId: string | null): boolean {
  if (context.cause === 'enter') return true;
  return context.cause === 'stay' && context.params.id !== shownId;
}

export async function docBeforeLoad(
  context: DocBeforeLoadContext,
  deps: DocBeforeLoadDeps = DEFAULT_DEPS,
): Promise<{ me: MeResponse }> {
  const shown = deps.shown ?? SHOWN;
  const opening = opensDocument(context, shown.id);
  if (context.cause !== 'preload') shown.id = context.params.id;
  if (opening) {
    markOpen('before-load');
    deps.prefetch(context.params.id);
  }
  const me = await deps.requireSession(context.location.pathname);
  if (opening) markOpen('auth-me');
  return { me };
}
